import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';

interface AgeGroup {
  id: number;
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min: string;
  birth_date_max: string;
}

interface AddAgeGroupFormProps {
  onSubmit: (data: { group_name: string; sex: 'male' | 'female'; birth_date_min: string; birth_date_max: string }) => void;
  onEditSubmit: (group: AgeGroup) => void;
  error: string | null;
  editingGroup: AgeGroup | null;
  onCancel: () => void;
}

export function AddAgeGroupForm({ onSubmit, onEditSubmit, error, editingGroup, onCancel }: AddAgeGroupFormProps) {
  const [groupName, setGroupName] = useState('');
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [birthDateMin, setBirthDateMin] = useState('');
  const [birthDateMax, setBirthDateMax] = useState('');

  useEffect(() => {
    if (editingGroup) {
      setGroupName(editingGroup.group_name);
      setSex(editingGroup.sex);
      setBirthDateMin(editingGroup.birth_date_min);
      setBirthDateMax(editingGroup.birth_date_max);
    } else {
      resetForm();
    }
  }, [editingGroup]);

  const resetForm = () => {
    setGroupName('');
    setSex('male');
    setBirthDateMin('');
    setBirthDateMax('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingGroup) {
      onEditSubmit({
        ...editingGroup,
        group_name: groupName,
        sex,
        birth_date_min: birthDateMin,
        birth_date_max: birthDateMax,
      });
    } else {
      onSubmit({
        group_name: groupName,
        sex,
        birth_date_min: birthDateMin,
        birth_date_max: birthDateMax,
      });
      resetForm();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="groupName">Age Group Name</Label>
          <Input
            id="groupName"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g., U10"
          />
        </div>
        <div>
          <Label htmlFor="birthDateMin">Minimum Birth Date</Label>
          <Input
            id="birthDateMin"
            type="date"
            value={birthDateMin}
            onChange={(e) => setBirthDateMin(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="birthDateMax">Maximum Birth Date</Label>
          <Input
            id="birthDateMax"
            type="date"
            value={birthDateMax}
            onChange={(e) => setBirthDateMax(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sex">Sex</Label>
          <Select value={sex} onValueChange={(value: 'male' | 'female') => setSex(value)}>
            <SelectTrigger id="sex">
              <SelectValue placeholder="Select sex" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Boys</SelectItem>
              <SelectItem value="female">Girls</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full">
            {editingGroup ? 'Update Age Group' : 'Add Age Group'}
          </Button>
        </div>
      </form>
      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Dialog open={!!editingGroup} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Age Group</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="editGroupName">Age Group Name</Label>
              <Input
                id="editGroupName"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., U10"
              />
            </div>
            <div>
              <Label htmlFor="editBirthDateMin">Minimum Birth Date</Label>
              <Input
                id="editBirthDateMin"
                type="date"
                value={birthDateMin}
                onChange={(e) => setBirthDateMin(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="editBirthDateMax">Maximum Birth Date</Label>
              <Input
                id="editBirthDateMax"
                type="date"
                value={birthDateMax}
                onChange={(e) => setBirthDateMax(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="editSex">Sex</Label>
              <Select value={sex} onValueChange={(value: 'male' | 'female') => setSex(value)}>
                <SelectTrigger id="editSex">
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Boys</SelectItem>
                  <SelectItem value="female">Girls</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onCancel} type="button">
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}